import { type FC, useCallback, useMemo, useState } from 'react';
import { TPO_METRIC_KPI_VIEW_MODAL, TPO_METRIC_METRIC_TYPE_MODAL } from '../../config/testIds';
import { useCustomerConfig, useTpoGenConfig } from '../../context/CustomerConfigContext';
import { usePlanDetailsContext } from '../context/PlanDetailsContext';
import ReportExportModal from '../../ReportExport/ReportExportModal';
import { buildPromoMetricExportPayload } from '../../ReportExport/utils/exportPayloadBuilders';
import type { ExportScope } from '../../ReportExport/types';
import usePromoMetricData from './hooks/queries/usePromoMetricData';
import useMetricSort from './hooks/ui/useMetricSort';
import { usePromoMetricColumns } from './hooks/ui/useMetricColumns';
import { orderKpisByCatalog } from './utils/orderKpisByCatalog';
import MetricTable from './MetricTable';
import PlanDetailsFooter from '../components/PlanDetailsFooter';
import ViewSelectModal from '../components/ViewSelectModal';
import KpiViewMetricBreadcrumb from './components/KpiViewMetricBreadcrumb';
import type { KpiViewType, MetricType, PromoMetricRow, TimePeriod } from './types';

interface PromoMetricViewProps {
    planId: string;
    dimensions: string[];
    kpis: string[];
    timePeriod: TimePeriod;
    searchKeyword: string;
    kpiViewType: KpiViewType;
    setKpiViewType: (v: KpiViewType) => void;
    metricType: MetricType;
    setMetricType: (v: MetricType) => void;
    showExportModal: boolean;
    onCloseExportModal: () => void;
}

const PromoMetricView: FC<PromoMetricViewProps> = ({
    planId,
    dimensions,
    kpis,
    timePeriod,
    searchKeyword,
    kpiViewType,
    setKpiViewType,
    metricType,
    setMetricType,
    showExportModal,
    onCloseExportModal,
}) => {
    const { currentPlanDetails, metricFilterState } = usePlanDetailsContext();
    const { promoKpiCatalog } = useCustomerConfig();
    const { metricPageConfig } = useTpoGenConfig();
    const [showKpiViewModal, setShowKpiViewModal] = useState(false);
    const [showMetricTypeModal, setShowMetricTypeModal] = useState(false);

    const { sortBy, sortOrder, handleTableChange, getColumnSortOrder } = useMetricSort('');

    // Columns follow `promo_metric_grid.requestable_kpis[].order` (the dropdown
    // list), not the order the user clicked. A late pick like `offer_type`
    // (catalog order 5) must slot in among the date FYI columns, not at the end.
    const orderedKpis = useMemo(
        () => orderKpisByCatalog(kpis, promoKpiCatalog),
        [kpis, promoKpiCatalog],
    );

    const data = usePromoMetricData({
        planId,
        dimensions,
        kpis: orderedKpis,
        kpiViewType,
        metricType,
        timePeriod,
        searchKeyword,
        sharedFilters: metricFilterState.filters,
        sortBy,
        sortOrder,
    });

    const handleClickKpiView = useCallback(() => setShowKpiViewModal(true), []);
    const handleClickMetric = useCallback(() => setShowMetricTypeModal(true), []);

    const columns = usePromoMetricColumns({
        dimensions,
        kpis: orderedKpis,
        kpiViewType,
        metricType,
        getColumnSortOrder,
        onClickKpiView: handleClickKpiView,
        onClickMetric: handleClickMetric,
    });

    const handleApplyKpiView = useCallback(
        (v: KpiViewType) => {
            setKpiViewType(v);
            setShowKpiViewModal(false);
        },
        [setKpiViewType],
    );

    const handleApplyMetricType = useCallback(
        (v: MetricType) => {
            setMetricType(v);
            setShowMetricTypeModal(false);
        },
        [setMetricType],
    );

    const allKpiKeys = useMemo(() => promoKpiCatalog.map((kpi) => kpi.key), [promoKpiCatalog]);

    // `dimensions` is never widened for `all_data`: it sets row granularity, so
    // selecting every one of them is a cross-product nobody asked for.
    const buildPromoMetricPayload = useCallback(
        (scope: ExportScope) =>
            buildPromoMetricExportPayload({
                scope,
                dimensions,
                kpis: orderedKpis,
                allKpiKeys,
                kpiViewType,
                metricType,
                timePeriod,
                filters: metricFilterState.filters ?? null,
                searchString: searchKeyword,
                sortBy,
                sortingOrder: sortOrder,
                totalCount: data.totalCount,
            }),
        [
            dimensions,
            orderedKpis,
            allKpiKeys,
            kpiViewType,
            metricType,
            timePeriod,
            metricFilterState.filters,
            searchKeyword,
            sortBy,
            sortOrder,
            data.totalCount,
        ],
    );

    return (
        <>
            <KpiViewMetricBreadcrumb kpiViewType={kpiViewType} metricType={metricType} />
            <MetricTable<PromoMetricRow>
                dataSource={data.dataSource}
                columns={columns}
                isPending={data.isPending}
                isFetching={data.isFetching}
                isFetchingNextPage={data.isFetchingNextPage}
                hasNextPage={data.hasNextPage}
                fetchNextPage={data.fetchNextPage}
                handleTableChange={handleTableChange}
            />
            <PlanDetailsFooter
                loadedCount={data.loadedCount}
                totalCount={data.totalCount}
                createdBy={currentPlanDetails?.created_by}
            />
            <ReportExportModal
                open={showExportModal}
                onClose={onCloseExportModal}
                planId={planId}
                reportKind="promo_metric"
                buildPayload={buildPromoMetricPayload}
            />
            <ViewSelectModal<KpiViewType>
                open={showKpiViewModal}
                title="KPI View"
                label="Choose KPI view"
                options={metricPageConfig.kpiViewTypeOptions}
                initialValue={kpiViewType}
                onCancel={() => setShowKpiViewModal(false)}
                onApply={handleApplyKpiView}
                testId={TPO_METRIC_KPI_VIEW_MODAL}
            />
            <ViewSelectModal<MetricType>
                open={showMetricTypeModal}
                title="Metric"
                label="Choose Metric"
                options={metricPageConfig.metricTypeOptions}
                initialValue={metricType}
                onCancel={() => setShowMetricTypeModal(false)}
                onApply={handleApplyMetricType}
                testId={TPO_METRIC_METRIC_TYPE_MODAL}
            />
        </>
    );
};

PromoMetricView.displayName = 'PromoMetricView';
export default PromoMetricView;
